import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SignatureInfo } from '@consolidocs-ui/types/file-and-folder';
import { StatusTooltip } from './StatusTooltip';
import './SignatureChip.scss';

export interface SignatureFacts {
  file_type?: string | null;
  is_signed?: boolean;
  signature_info?: SignatureInfo[];
  is_consolidated?: boolean;
  status?: { code?: string } | null;
}

export type SignatureState = 'partial' | 'unsigned';

const DERIVED_FILE_TYPES = ['summary', 'consolidated'];

export const getSignatureState = (
  file: SignatureFacts | undefined
): SignatureState | null => {
  if (!file) return null;
  if (file.is_signed === true) return null;
  /* Its signatures haven't been read yet, so `is_signed: false` there means
     not known, not unsigned. */
  if (file.status?.code === 'processing') return null;
  if (file.file_type && DERIVED_FILE_TYPES.includes(file.file_type)) {
    return null;
  }

  const parties = file.signature_info;

  if (file.is_signed !== false) {
    if (!parties?.length) return null;
    if (parties.every((party) => party.signed)) return null;
  }

  return parties?.some((party) => party.signed) ? 'partial' : 'unsigned';
};

export interface SignatureChipProps {
  state: SignatureState;
  signatures?: SignatureInfo[];
}

/**
 * Temporarily hides the signature chip on file rows.
 * The component is intentionally left in place - flip to `true` to restore.
 */
export const SHOW_SIGNATURE_CHIP = false;

export const SignatureChip = React.memo(
  ({ state, signatures }: SignatureChipProps) => {
    const { t } = useTranslation();

    if (!SHOW_SIGNATURE_CHIP) {
      return null;
    }

    const unnamed = t('Unnamed party');
    const named = (parties: SignatureInfo[]) =>
      parties.map((party) => party.party_name || unnamed).join(', ');

    const signed = (signatures ?? []).filter((party) => party.signed);
    const awaiting = (signatures ?? []).filter((party) => !party.signed);

    const title = (
      <>
        {signed.length > 0 && (
          <div>{t('Signed by {{parties}}', { parties: named(signed) })}</div>
        )}
        {awaiting.length > 0 ? (
          <div>{t('Awaiting {{parties}}', { parties: named(awaiting) })}</div>
        ) : (
          signed.length === 0 && <div>{t('No signature on record')}</div>
        )}
      </>
    );

    return (
      <StatusTooltip title={title} disableInteractive leaveDelay={0}>
        <span
          className={`tree-signature-chip tree-signature-chip--${state}`}
          data-testid="tree-signature-chip"
        >
          {state === 'partial' ? t('Partial') : t('Unsigned')}
        </span>
      </StatusTooltip>
    );
  }
);
